// Decode only tag-shaped entities, preserving ordinary comparison symbols.
export const decodeNexusMarkup = (raw) => String(raw || '').replace(/&(?:amp;)+(lt|gt|#0*60|#0*62|#x0*3c|#x0*3e);/gi, '&$1;').replace(
  /&(?:amp;)*(?:lt|#0*60|#x0*3c);(\/?[a-z][^<>]*?)&(?:amp;)*(?:gt|#0*62|#x0*3e);/gi,
  (_, tag) => `<${tag}>`
);
export const NEXUS_DROP_TAGS = new Set(['script', 'iframe', 'object', 'embed', 'img', 'style', 'form', 'input', 'button', 'textarea', 'select', 'svg', 'math', 'template']);
const allowed = new Set(['details', 'summary', 'h3', 'p', 'ul', 'ol', 'li', 'b', 'strong', 'em', 'i', 'br']);

// Inert parsing only. No source attributes are serialized.
export const sanitizeNexusMarkup = (raw) => {
  const doc = new DOMParser().parseFromString(decodeNexusMarkup(raw), 'text/html');
  const escape = (text) => text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const walk = (node) => {
    if (node.nodeType === 3) return escape(node.nodeValue || '');
    if (node.nodeType !== 1) return '';
    const tag = node.tagName.toLowerCase();
    if (NEXUS_DROP_TAGS.has(tag)) return '';
    const body = Array.from(node.childNodes).map(walk).join('');
    if (!allowed.has(tag)) return body;
    return tag === 'br' ? '<br>' : `<${tag}>${body}</${tag}>`;
  };
  return Array.from(doc.body.childNodes).map(walk).join('');
};
export const nexusMarkupToText = (raw) => {
  const doc = new DOMParser().parseFromString(sanitizeNexusMarkup(raw).replace(/<br>|<\/(?:p|li|h3|summary|details)>/g, '\n'), 'text/html');
  return (doc.body.textContent || '').replace(/[*#`]/g, '').trim();
};

// Convert only approved, inert Nexus HTML-lite tokens into PDF-friendly text.
// No DOM insertion occurs and all source attributes are discarded.
export const normalizeNexusMarkupForPdf = (raw) => {
  let source = sanitizeNexusMarkup(raw).replace(
    /&lt;(\/?)(h3|p|ul|ol|li|b|strong|em|br)(?:\s[^&]*?)?\s*\/?&gt;/gi,
    (_match, closing, tag) => `<${closing || ''}${tag.toLowerCase()}>`
  );
  source = source.replace(/<ol[^>]*>([\s\S]*?)<\/ol\s*>/gi, (_match, listBody) => {
    let itemNumber = 0;
    return `\n${listBody
      .replace(/<li[^>]*>/gi, () => `${++itemNumber}. `)
      .replace(/<\/li\s*>/gi, '\n')}\n`;
  });
  return source
    .replace(/<h3[^>]*>/gi, '\n### ')
    .replace(/<\/h3\s*>/gi, '\n')
    .replace(/<p[^>]*>/gi, '\n')
    .replace(/<\/p\s*>/gi, '\n\n')
    .replace(/<\/?ul[^>]*>/gi, '\n')
    .replace(/<li[^>]*>/gi, '- ')
    .replace(/<\/li\s*>/gi, '\n')
    .replace(/<(?:b|strong)\b[^>]*>/gi, '**')
    .replace(/<\/(?:b|strong)\s*>/gi, '**')
    .replace(/<(?:em|i)\b[^>]*>/gi, '*')
    .replace(/<\/(?:em|i)\s*>/gi, '*')
    .replace(/<br\s*\/?>/gi, '\n');
};
